import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded'
import ActionsAPI from 'services/ActionsAPI'

export const CLEANUP_AGES = [
  { value: '1m', label: 'More than a month ago' },
  { value: '1y', label: 'More than a year ago' },
  { value: '2y', label: 'More than 2 years ago' },
  { value: '5y', label: 'More than 5 years ago' },
]

const plural = (n, word) => `${n.toLocaleString('en-GB')} ${word}${n === 1 ? '' : 's'}`

const OldSuggestionsCleanup = ({ bpId = null, onChanged }) => {
  const [age, setAge] = useState('1y')
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [running, setRunning] = useState(false)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    ActionsAPI.cleanupPreview({ olderThan: age, ...(bpId ? { buildingPassportId: bpId } : {}) })
      .then(res => { if (!cancelled) setPreview(res?.data?.data || null) })
      .catch(() => { if (!cancelled) setPreview(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [age, bpId, refresh])

  const count = preview?.suggestions || 0
  const ageLabel = CLEANUP_AGES.find(a => a.value === age)?.label.toLowerCase()

  const undo = async batchId => {
    try {
      const res = await ActionsAPI.cleanupUndo(batchId)
      const restored = res?.data?.data?.restoredCount || 0
      toast.success(`${plural(restored, 'suggestion')} put back for review.`)
    } catch {
      toast.error('Could not put them back. Please try again.')
    } finally {
      setRefresh(n => n + 1)
      onChanged?.()
    }
  }

  const run = async () => {
    setRunning(true)
    try {
      const res = await ActionsAPI.cleanupRun({ olderThan: age, ...(bpId ? { buildingPassportId: bpId } : {}) })
      const { dismissedCount = 0, batchId } = res?.data?.data || {}
      setConfirming(false)
      toast.success(({ closeToast }) => (
        <ToastLine>
          <span>{plural(dismissedCount, 'suggestion')} removed.</span>
          {batchId && dismissedCount > 0 && (
            <ToastUndo type='button' onClick={() => { closeToast?.(); undo(batchId) }}>Undo</ToastUndo>
          )}
        </ToastLine>
      ), { autoClose: 15000 })
      setRefresh(n => n + 1)
      onChanged?.()
    } catch {
      toast.error('Could not remove these suggestions. Please try again.')
    } finally {
      setRunning(false)
    }
  }

  return (
    <Panel aria-labelledby='old-suggestions-title'>
      <Head>
        <CleaningServicesRoundedIcon style={{ fontSize: 20, color: '#475467' }} aria-hidden='true' />
        <div>
          <Title id='old-suggestions-title'>Clear out suggestions from older documents</Title>
          <Lead>
            Old documents often describe problems that were fixed long ago. You can remove the AI suggestions
            that came from documents uploaded before a certain time, so you only need to check recent ones.
            Nothing that has already been approved is touched.
          </Lead>
        </div>
      </Head>
      <Row>
        <Ages role='group' aria-label='Documents uploaded'>
          <AgesLabel>Documents uploaded</AgesLabel>
          {CLEANUP_AGES.map(option => (
            <AgeChip key={option.value} type='button' aria-pressed={age === option.value} $on={age === option.value} onClick={() => setAge(option.value)}>
              {option.label}
            </AgeChip>
          ))}
        </Ages>
        <RemoveBtn type='button' disabled={loading || count === 0 || running} onClick={() => setConfirming(true)}>
          {loading ? 'Counting…' : count === 0 ? 'Nothing to remove' : `Remove ${plural(count, 'suggestion')}`}
        </RemoveBtn>
      </Row>
      {!loading && count > 0 && (
        <Note>{plural(count, 'suggestion')} from {plural(preview.documents, 'document')} uploaded {ageLabel}{preview.buildings > 1 ? `, across ${plural(preview.buildings, 'building')}` : ''}.</Note>
      )}

      <Dialog open={confirming} onClose={() => !running && setConfirming(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Remove {plural(count, 'suggestion')}?</DialogTitle>
        <DialogContent>
          <ConfirmText>
            These AI suggestions come from documents uploaded {ageLabel}. They will be marked as not a real issue and
            will not count as remedials. You can undo this straight afterwards.
          </ConfirmText>
        </DialogContent>
        <DialogActions>
          <CancelBtn type='button' disabled={running} onClick={() => setConfirming(false)}>Cancel</CancelBtn>
          <RemoveBtn type='button' disabled={running} onClick={run}>{running ? 'Removing…' : `Remove ${plural(count, 'suggestion')}`}</RemoveBtn>
        </DialogActions>
      </Dialog>
    </Panel>
  )
}

export default OldSuggestionsCleanup

const Panel = styled.section`
  margin: 12px 0;
  padding: 14px 16px;
  border: 1px solid #e4e6ee;
  border-radius: 12px;
  background: #f7f8fc;
`
const Head = styled.div` display: flex; gap: 10px; align-items: flex-start; `
const Title = styled.h3` margin: 0 0 4px; font-size: 16px; font-weight: 700; color: #101828; `
const Lead = styled.p` margin: 0; font-size: 14px; line-height: 1.5; color: #475467; `
const Row = styled.div` display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-top: 12px; `
const Ages = styled.div` display: flex; align-items: center; flex-wrap: wrap; gap: 6px; `
const AgesLabel = styled.span` font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #667085; margin-right: 4px; `
const AgeChip = styled.button`
  font: inherit; font-size: 14px; font-weight: 600; min-height: 40px; padding: 0 14px; border-radius: 20px; cursor: pointer;
  border: 1px solid ${({ $on }) => ($on ? '#0c1229' : '#d0d5dd')};
  background: ${({ $on }) => ($on ? '#0c1229' : '#fff')};
  color: ${({ $on }) => ($on ? '#fff' : '#0c1229')};
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
const RemoveBtn = styled.button`
  font: inherit; font-size: 14px; font-weight: 700; min-height: 40px; padding: 0 16px; border-radius: 8px; cursor: pointer;
  border: 1px solid #b00020; background: #fff; color: #b00020;
  &:hover:not(:disabled) { background: #fdecea; }
  &:disabled { opacity: 0.55; cursor: default; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
const CancelBtn = styled.button`
  font: inherit; font-size: 14px; font-weight: 600; min-height: 40px; padding: 0 16px; border-radius: 8px; cursor: pointer;
  border: 1px solid #d0d5dd; background: #fff; color: #0c1229;
`
const Note = styled.p` margin: 8px 0 0; font-size: 13px; color: #475467; `
const ConfirmText = styled.p` margin: 0; font-size: 15px; line-height: 1.5; color: #344054; `
const ToastLine = styled.div` display: flex; align-items: center; gap: 12px; `
const ToastUndo = styled.button`
  font: inherit; font-weight: 700; color: #1d7f8a; background: #fff; border: 1px solid #33becc;
  border-radius: 6px; min-height: 34px; padding: 0 12px; cursor: pointer;
`
